/**
 * Formulaire résa « Quand ? » :
 * - Aucune requête de disponibilité au chargement.
 * - Dates affichées uniquement après sélection au calendrier (style Airbnb).
 * - Au submit : construction de l’URL (arrival, departure, nbPers, etc.) et navigation côté client — pas de 302.
 */

import { store, getContext, getElement } from '@wordpress/interactivity';
import { buildSearchWithEncodedParams } from '../shared/url-state';

const getDatesAnchorField = (formEl) => {
	if (!formEl) return null;
	const merged = formEl.querySelector('.gt-caleran-merged');
	if (merged) return merged.closest('.gt-form-field') || null;
	const arrival = formEl.querySelector('.gt-caleran-arrival');
	if (arrival) return arrival.closest('.gt-form-field') || null;
	const departure = formEl.querySelector('.gt-caleran-departure');
	if (departure) return departure.closest('.gt-form-field') || null;
	return null;
};

const placePopoverAroundAnchor = (popoverEl, anchorFieldEl) => {
	if (!popoverEl || !anchorFieldEl) return;

	// Mesurer l'espace dispo dans le viewport
	const anchorRect = anchorFieldEl.getBoundingClientRect();
	const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
	const spaceAbove = anchorRect.top;
	const spaceBelow = Math.max(0, viewportH - anchorRect.bottom);

	// Taille de la pop-up (fallback si pas encore mesurable)
	const popRect = popoverEl.getBoundingClientRect();
	const popH = popRect.height || 140;
	const required = popH + 18; // marge + flèche

	const shouldPlaceAbove = spaceAbove >= required && spaceAbove >= spaceBelow;

	popoverEl.classList.toggle('is-above', shouldPlaceAbove);
	popoverEl.classList.toggle('is-below', !shouldPlaceAbove);
};

const syncStickyButtonState = (wrapper) => {
	if (!wrapper) return;
	const isOpen = wrapper.classList.contains('toggled');
	const btnOpen = wrapper.querySelector('.gt-ws-ctv-resa-sticky-button--open');
	const btnClose = wrapper.querySelector('.gt-ws-ctv-resa-sticky-button--close');
	if (btnOpen) btnOpen.style.display = isOpen ? 'none' : '';
	if (btnClose) btnClose.style.display = isOpen ? '' : 'none';
};

const isStickyOnMobileLazy = (formEl) =>
	formEl && formEl.classList.contains('sticked-on-mobile') && typeof window !== 'undefined' && window.innerWidth < 1024;

/**
 * Initialise l’observer sticky pour un formulaire lazy (affichage/masquage du bandeau au scroll).
 */
const initStickyObserverForElLazy = (formEl) => {
	if (!formEl || !formEl.classList.contains('is-sticky')) return;
	const wrapper = formEl.closest('.wp-block-gt-gt-ws-ctv-form-resa-lazy') || formEl.parentElement;
	if (!wrapper) return;

	if (formEl.classList.contains('sticked-on-mobile') && typeof window !== 'undefined' && window.innerWidth >= 1024) {
		formEl.classList.remove('sticked');
	}
	if (formEl._stickyObserverInitialized && formEl._stickySentinel && formEl._stickySentinel.isConnected) return;

	if (formEl._stickyActivationObserver) formEl._stickyActivationObserver.disconnect();
	if (formEl._stickyDeactivationObserver) formEl._stickyDeactivationObserver.disconnect();
	if (formEl._stickySentinel && formEl._stickySentinel.isConnected) formEl._stickySentinel.remove();

	const sentinel = document.createElement('div');
	sentinel.style.cssText = 'height:1px;width:1px;pointer-events:none;visibility:hidden;margin:0;padding:0;border:none;';
	wrapper.after(sentinel);

	const activationObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) formEl.classList.add('sticked');
			});
		},
		{ threshold: 0, rootMargin: '-100px 0px 0px 0px' }
	);
	const deactivationObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !isStickyOnMobileLazy(formEl) && !formEl._caleranOpen) {
					formEl.classList.remove('sticked');
					formEl.classList.remove('toggled');
					syncStickyButtonState(wrapper);
				}
			});
		},
		{ threshold: 0, rootMargin: '0px 0px 0px 0px' }
	);
	activationObserver.observe(sentinel);
	deactivationObserver.observe(sentinel);
	formEl._stickyObserverInitialized = true;
	formEl._stickySentinel = sentinel;
	formEl._stickyActivationObserver = activationObserver;
	formEl._stickyDeactivationObserver = deactivationObserver;

	if (formEl.classList.contains('sticked-on-mobile') && !formEl._stickyResizeListener) {
		formEl._stickyResizeListener = () => {
			if (isStickyOnMobileLazy(formEl)) formEl.classList.add('sticked');
		};
		window.addEventListener('resize', formEl._stickyResizeListener);
	}
};

const initAllStickyObserversLazy = () => {
	document.querySelectorAll('.gt-ws-ctv-form-resa-lazy.is-sticky').forEach((formEl) => {
		initStickyObserverForElLazy(formEl);
		syncStickyButtonState(formEl);
	});
};

const { state } = store('gt/ctv-quand', {
	actions: {
		sortHebergements: () => {},
		toggleStickyForm: () => {
			const element = getElement();
			const wrapper = element?.ref?.closest?.('.wp-block-gt-gt-ws-ctv-form-resa-lazy');
			if (wrapper) {
				wrapper.classList.toggle('toggled');
				syncStickyButtonState(wrapper);
			}
		},
		closeStickyFormOnScroll: () => {},
		initStickyObserver: () => {
			const element = getElement();
			if (element?.ref) initStickyObserverForElLazy(element.ref);
		},
		changeNbPers: (e) => {
			const context = getContext();
			const nbPers = e.target.value;
			context.gtNbPers = nbPers;
			state.gtNbPers = nbPers;
			if (typeof Cookies !== 'undefined') Cookies.set('gtNbPers', nbPers);
		},
		changeType: (e) => {
			const context = getContext();
			const element = getElement();
			const selectedType = e.target.value;
			const typeArray = selectedType ? [selectedType] : [];
			context.gtType = typeArray;
			state.gtType = typeArray;
			if (selectedType && typeof Cookies !== 'undefined') Cookies.set('gtType', selectedType);
			else if (typeof Cookies !== 'undefined') Cookies.remove('gtType');
			if (context.showSearch === false) {
				const form = element?.ref?.closest('form');
				if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
			}
		},
		changeCamping: (e) => {
			const context = getContext();
			const gtCamping = e.target.value;
			context.gtCamping = gtCamping;
			state.gtCamping = gtCamping;
		},
		dismissDatesRequiredPopover: () => {
			state.showDatesRequiredError = false;
			state.datesErrorFormId = '';
			document.querySelectorAll('.gt-ws-ctv-form-resa-lazy__dates-error-popover').forEach((el) => { el.hidden = true; });
		},
		submitForm: (e) => {
			if (e) e.preventDefault();
			const form = e?.target?.closest?.('form') || e?.target;
			if (!form || form.tagName !== 'FORM') return;
			if (!state.gtArrival || !state.gtDeparture) {
				state.showDatesRequiredError = true;
				state.datesErrorFormId = form.id || '';
				const pop = form.querySelector('.gt-ws-ctv-form-resa-lazy__dates-error-popover');
				if (pop) {
					// Ancrer la pop-up sur le champ dates (sans changer le HTML source).
					const anchorField = getDatesAnchorField(form);
					if (anchorField && pop.parentElement !== anchorField) {
						anchorField.appendChild(pop);
					}
					pop.hidden = false;
					// Appliquer le placement (au-dessus / en-dessous) selon le viewport
					placePopoverAroundAnchor(pop, anchorField);
					// Re-mesure après paint (le temps que hidden soit retiré)
					requestAnimationFrame(() => placePopoverAroundAnchor(pop, anchorField));
					pop.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
				}
				return;
			}
			state.showDatesRequiredError = false;
			state.datesErrorFormId = '';

			const context = getContext();
			const baseUrl = context.destination || window.location.origin + window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('arrival', state.gtArrival);
			url.searchParams.set('departure', state.gtDeparture);
			url.searchParams.set('nbPers', state.gtNbPers || '2');
			if (state.gtType && state.gtType.length) url.searchParams.set('gtType', state.gtType[0]);
			const campingVal = Array.isArray(state.gtCamping) ? state.gtCamping[0] : state.gtCamping;
			if (campingVal) url.searchParams.set('gtCamping', campingVal);
			url.search = buildSearchWithEncodedParams(url);

			if (typeof Cookies !== 'undefined') {
				Cookies.set('gtArrival', state.gtArrival);
				Cookies.set('gtDeparture', state.gtDeparture);
				Cookies.set('gtNbPers', state.gtNbPers || '2');
			}
			window.location.href = url.toString();
		},
	},
});

/**
 * Init Caleran : au clic sur le champ (placeholders selon fusion / séparés), le calendrier s’ouvre ;
 * les dates ne s’affichent qu’après sélection.
 */
const initCaleranQuand = () => {
	if (typeof window.jQuery === 'undefined' || typeof window.moment === 'undefined') return;
	if (!window.jQuery?.fn?.caleran) return;

	const language = document.documentElement.lang?.slice(0, 2) || 'fr';
	const caleranButtonLabels = language === 'en'
		? { cancelLabel: 'Cancel', applyLabel: 'Apply' }
		: { cancelLabel: 'Annuler', applyLabel: 'Valider' };

	const prevMonthIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
	const nextMonthIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

	const generateRanges = (arrivalDate) => {
		const a = moment(arrivalDate);
		const getNext = (date, w) => { let d = w - date.day(); if (d <= 0) d += 7; return date.clone().add(d, 'days'); };
		return [
			{ title: '1 semaine', startDate: a, endDate: a.clone().add(1, 'weeks') },
			{ title: '2 semaines', startDate: a, endDate: a.clone().add(2, 'weeks') },
			{ title: '3 semaines', startDate: a, endDate: a.clone().add(3, 'weeks') },
			{ title: 'Du mercredi au mercredi', startDate: getNext(a, 3), endDate: getNext(a, 3).clone().add(1, 'weeks') },
			{ title: 'Du vendredi au vendredi', startDate: getNext(a, 5), endDate: getNext(a, 5).clone().add(1, 'weeks') },
			{ title: 'Du samedi au samedi', startDate: getNext(a, 6), endDate: getNext(a, 6).clone().add(1, 'weeks') },
		];
	};

	document.querySelectorAll('.gt-ws-ctv-form-resa-lazy').forEach((formEl) => {
		if (!formEl._gtLazyDismissPopoverCapture) {
			formEl._gtLazyDismissPopoverCapture = true;
			formEl.addEventListener(
				'pointerdown',
				(ev) => {
					const el = ev.target;
					if (
						el?.matches?.('.gt-caleran-merged, .gt-caleran-arrival, .gt-caleran-departure') ||
						el?.closest?.('.gt-caleran-merged, .gt-caleran-arrival, .gt-caleran-departure')
					) {
						state.showDatesRequiredError = false;
						state.datesErrorFormId = '';
					}
				},
				true
			);
		}

		if (!formEl._gtLazyDismissPopoverOnScroll) {
			formEl._gtLazyDismissPopoverOnScroll = true;
			// Fermer la pop-up au scroll (même si le scroll est déclenché par le sticky/caleran)
			document.addEventListener(
				'scroll',
				() => {
					if (state.showDatesRequiredError && state.datesErrorFormId === formEl.id) {
						state.showDatesRequiredError = false;
						state.datesErrorFormId = '';
						const pop = formEl.querySelector('.gt-ws-ctv-form-resa-lazy__dates-error-popover');
						if (pop) pop.hidden = true;
					}
				},
				{ passive: true, capture: true }
			);
		}

		const mergedInput = formEl.querySelector('.gt-caleran-merged');
		const arrivalInput = formEl.querySelector('.gt-caleran-arrival');
		const departureInput = formEl.querySelector('.gt-caleran-departure');

		const minDate = state.openingDate && moment(state.openingDate).isValid() && moment(state.openingDate) > moment()
			? moment(state.openingDate) : moment();
		const maxDate = state.closingDate && moment(state.closingDate).isValid() ? moment(state.closingDate) : moment().add(1, 'year');

		// 1) Ne jamais pré-remplir sans paramètres d'URL : on force les champs à vide.
		if (mergedInput) mergedInput.value = '';
		if (arrivalInput) arrivalInput.value = '';
		if (departureInput) departureInput.value = '';
		state.gtArrival = '';
		state.gtDeparture = '';

		// 2) Si l'URL contient déjà arrival/departure, on les affiche dans le formulaire.
		let hasUrlDates = false;
		try {
			const url = new URL(window.location.href);
			const arrivalParam = url.searchParams.get('arrival');
			const departureParam = url.searchParams.get('departure');
			const nbPersParam = url.searchParams.get('nbPers');
			const nbPersSelect = formEl.querySelector('.gt-select-nbpers');

			const parsedArrival =
				arrivalParam &&
				(moment(arrivalParam, 'DD/MM/YYYY', true).isValid()
					? moment(arrivalParam, 'DD/MM/YYYY')
					: moment(arrivalParam, 'YYYY-MM-DD', true).isValid()
						? moment(arrivalParam, 'YYYY-MM-DD')
						: null);

			const parsedDeparture =
				departureParam &&
				(moment(departureParam, 'DD/MM/YYYY', true).isValid()
					? moment(departureParam, 'DD/MM/YYYY')
					: moment(departureParam, 'YYYY-MM-DD', true).isValid()
						? moment(departureParam, 'YYYY-MM-DD')
						: null);

			if (parsedArrival && parsedDeparture) {
				hasUrlDates = true;
				state.showDatesRequiredError = false;
				state.datesErrorFormId = '';
				state.gtArrival = parsedArrival.format('DD/MM/YYYY');
				state.gtDeparture = parsedDeparture.format('DD/MM/YYYY');
				if (mergedInput) {
					mergedInput.value = `${state.gtArrival} - ${state.gtDeparture}`;
				}
				if (arrivalInput) {
					arrivalInput.value = state.gtArrival;
				}
				if (departureInput) {
					departureInput.value = state.gtDeparture;
				}
			}

			// Met à jour le nombre de personnes depuis l'URL, même si les dates ne sont pas présentes.
			if (nbPersParam) {
				const n = parseInt(nbPersParam, 10);
				if (!Number.isNaN(n) && n >= 1 && n <= 99) {
					state.gtNbPers = String(n);
					if (nbPersSelect) {
						nbPersSelect.value = String(n);
					}
					if (typeof Cookies !== 'undefined') {
						Cookies.set('gtNbPers', String(n));
					}
				}
			}
		} catch (e) {
			// En cas d'environnement sans URL API, on ignore.
		}

		const applySelect = (startDate, endDate) => {
			state.showDatesRequiredError = false;
			state.datesErrorFormId = '';
			state.gtArrival = startDate.format('DD/MM/YYYY');
			state.gtDeparture = endDate.format('DD/MM/YYYY');
			if (typeof Cookies !== 'undefined') {
				Cookies.set('gtArrival', state.gtArrival);
				Cookies.set('gtDeparture', state.gtDeparture);
			}
			if (mergedInput) mergedInput.value = `${state.gtArrival} - ${state.gtDeparture}`;
			if (arrivalInput) arrivalInput.value = state.gtArrival;
			if (departureInput) departureInput.value = state.gtDeparture;
		};

		if (mergedInput) {
			const $m = window.jQuery(mergedInput);
			if ($m.data('caleran')) return;
			$m.caleran({
				startDate: null,
				endDate: null,
				minDate,
				maxDate,
				hideOutOfRange: true,
				enableSwipe: true,
				startOnMonday: true,
				enableKeyboard: true,
				autoCloseOnSelect: true,
				locale: language,
				cancelLabel: caleranButtonLabels.cancelLabel,
				applyLabel: caleranButtonLabels.applyLabel,
				enableMonthSwitcher: true,
				enableYearSwitcher: true,
				prevMonthIcon,
				nextMonthIcon,
				ranges: generateRanges(moment()),
				format: 'DD/MM/YYYY',
				onbeforeshow: () => { formEl._caleranOpen = true; },
				onhide: () => { formEl._caleranOpen = false; },
				onafterselect: (caleran, start, end) => {
					applySelect(start, end);
					$m.val(`${state.gtArrival} - ${state.gtDeparture}`);
				},
			});

			// Si aucune date dans l'URL, on s'assure que le champ reste vide après l'init Caleran.
			if (!hasUrlDates) {
				$m.val('');
			}
		}

		if (arrivalInput && departureInput) {
			const $arr = window.jQuery(arrivalInput);
			const $dep = window.jQuery(departureInput);
			if ($arr.data('caleran') || $dep.data('caleran')) return;
			let startDate = null;
			let endDate = null;
			$arr.caleran({
				startDate,
				endDate,
				minDate,
				maxDate,
				hideOutOfRange: true,
				enableSwipe: true,
				startOnMonday: true,
				locale: language,
				cancelLabel: caleranButtonLabels.cancelLabel,
				applyLabel: caleranButtonLabels.applyLabel,
				prevMonthIcon,
				nextMonthIcon,
				ranges: generateRanges(moment()),
				format: 'DD/MM/YYYY',
				onbeforeshow: () => { formEl._caleranOpen = true; },
				onhide: () => { formEl._caleranOpen = false; },
				onfirstselect: (caleran, start) => {
					startDate = start.clone();
					$dep.caleran('show');
				},
				onafterselect: (caleran, start, end) => {
					startDate = start;
					endDate = end;
					applySelect(start, end);
				},
			});
			$dep.caleran({
				startDate,
				endDate,
				minDate,
				maxDate,
				hideOutOfRange: true,
				enableSwipe: true,
				startOnMonday: true,
				locale: language,
				cancelLabel: caleranButtonLabels.cancelLabel,
				applyLabel: caleranButtonLabels.applyLabel,
				prevMonthIcon,
				nextMonthIcon,
				ranges: generateRanges(moment()),
				format: 'DD/MM/YYYY',
				onbeforeshow: () => { formEl._caleranOpen = true; },
				onhide: () => { formEl._caleranOpen = false; },
				onafterselect: (caleran, start, end) => {
					startDate = start;
					endDate = end;
					applySelect(start, end);
				},
			});

			// Même logique : si aucune date URL, on force les champs à vide après init.
			if (!hasUrlDates) {
				$arr.val('');
				$dep.val('');
			}
		}
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCaleranQuand);
} else {
	initCaleranQuand();
}
document.addEventListener('rocket-preload', initCaleranQuand);
document.addEventListener('wpr-ajaxified-loaded', initCaleranQuand);

// Sticky observer pour formulaire lazy (même logique que form-resa v1)
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAllStickyObserversLazy);
} else {
	initAllStickyObserversLazy();
}
document.addEventListener('rocket-preload', initAllStickyObserversLazy);
document.addEventListener('wpr-ajaxified-loaded', initAllStickyObserversLazy);
window.addEventListener('popstate', initAllStickyObserversLazy);

// Fermeture au clic en dehors du formulaire sticky lazy
document.addEventListener('click', (e) => {
	document.querySelectorAll('.gt-ws-ctv-form-resa-lazy.is-sticky.toggled').forEach((formEl) => {
		if (!formEl.contains(e.target) && !formEl._caleranOpen) {
			formEl.classList.remove('toggled');
			syncStickyButtonState(formEl);
		}
	});
}, true);
